/**
 * The Program view (build plan decision 6): the program as its lead roles see it. Roles with holders
 * and gaps, the roster with stale flags, the customer register, the model's cadences with their last
 * run and next due, customer asks with state and age, risks read off the record, contract events, the
 * weekly status draft, and conformance as fractions.
 *
 * Two rules shape every section. Nothing here comes from an unconfirmed source except the
 * unacknowledged-ask count and its age (v2, "The visibility rule"): a candidate on someone's private
 * list is invisible to this page whoever is looking, including its own prospective owner. And no
 * percentage is shown over a denominator the model owner has not defined (handoff §5, "the model as
 * code"), so cadences and gates with no evidence either way are reported as not measured rather than
 * as failures.
 *
 * The model is taken structurally (`ModelLike`) so this package stays free of I/O and of a dependency on
 * `@trellis/model`; the API passes the real model in.
 */
import { thresholdMs, unattended } from "./unattended.js";
import { LIVE_STATUSES, type Commitment, type Audit, type Iso, type PersonId, type ProgramId, type SourceKind } from "./commitment.js";
import { visibility, type Roster } from "./visibility.js";
import { cadencePeriods, tallyPeriods } from "./cadence-counts.js";
import { ackClock } from "./record.js";
import { cap, dateOf, daysBetween, fmtDate, fmtDateFull, fmtDay, fmtDayFull, fmtDuration, fmtStamp, numberWord, parseDuration, plural } from "./dates.js";
import { isHiddenDraft } from "./hidden-drafts.js";
import { ancestorIds, maySeeUnitView, STRUCTURE_RULE, subtreeIds } from "./structure.js";

// ------------------------------------------------------------------------------------------------ inputs

// Optional fields admit `undefined` explicitly so the zod-inferred Model (which writes `x?: T | undefined`)
// is assignable under exactOptionalPropertyTypes.
export interface ModelLike {
  version: string;
  parameters: { id: string; kind: string; default?: string | number | boolean | string[] | undefined }[];
  roles: { id: string; name: string; mayCombineWith?: string[] | undefined }[];
  cadences: { id: string; name: string; purpose?: string | undefined; frequency: { kind: string; weekday?: string | undefined; dayOfMonth?: string | undefined; event?: string | undefined; trigger?: string | undefined; offset?: string | undefined; leadTime?: string | undefined }; runsBy: string; evidence: string; missedIs?: string | undefined; scope?: "program" | "unit" | undefined; eligibleKinds?: ("workstream" | "team")[] | undefined }[];
  profiles: { id: string; name: string; cadences: string[] }[];
  artifacts?: { id: string; audience?: string | undefined }[] | undefined;
}

export interface ProgramLike {
  id: ProgramId;
  name: string;
  profile: string;
  modelVersion: string;
  corpdashContractId?: string | null;
  /** ProgramInstance: parameters and exceptions. Every customer noun lives here. */
  instance?: { parameters?: Record<string, unknown>; exceptions?: { rule: string; reason?: string; approvedBy?: string; expires?: string }[] } | null;
  customerRegister?: unknown;
}

export interface RegisterEntryLike { name: string; address: string; role?: string; note?: string; contractorMarker?: string }

/**
 * What a source's stored metadata says about an ask, keyed by the ask's `source.ref`. The caller
 * resolves identifiers to names before handing this over: a channel GUID or a bare address on
 * screen tells a reader nothing.
 *
 * How much of this reaches a reader is `askSourceDetailFor`, off the redaction the visibility rule
 * returns: the whole of it for the person whose source it is, the channel and the counterparty for a
 * lead reading intake, and the channel alone for everybody else.
 */
export interface AskSourceLike {
  kind: SourceKind;
  /** Whose mailbox, calendar or chat carried it. */
  ownerUpn?: PersonId | undefined;
  /** Mail: the mailbox it arrived in, by name. */
  to?: string | undefined;
  /** Mail: how many other addresses were on it. */
  alsoTo?: number | undefined;
  /** Chat: the team whose channel carried it, as the link cached its name. */
  team?: string | undefined;
  /** The subject line, meeting title or ticket summary. */
  title?: string | undefined;
  /** Topic phrases this contract configured that this source matched. */
  topics?: string[] | undefined;
}

/** Track B's cadence engine records runs; until its table lands this is the shape the view reads. */
/**
 * `commitmentId` is the cadence commitment the run opened. Marking a run missed leaves that
 * commitment open and past its date, so anything counting both has to know they are one event.
 */
/**
 * `missed` is "the period ended without evidence and nobody withdrew it". A withdrawn period is
 * neither ran nor missed but not measured, and the one place a row becomes this shape
 * (`apps/api/src/views.ts`) decides that once, so no reader has to spell the rule a second time
 * (docs/2026-09-26-obligation-states.md).
 */
export interface CadenceRunLike { cadenceId: string; periodStart?: Iso; periodEnd?: Iso; dueDate?: Iso | undefined; ranAt?: Iso | null; missed?: boolean; withdrawnAt?: Iso | undefined; evidenceRef?: string; byUpn?: PersonId; unitId?: string | undefined; commitmentId?: string | undefined }
export interface ContractEventLike {
  id: string; type: string; date?: Iso; state: string; title?: string; corpdashId?: string;
  /**
   * The three contract facts the header needs, and nothing else. A CorpDash row also
   * carries funding changes and client detail; finance lives in CorpDash, so the whole
   * payload never crosses this seam.
   */
  contract?: { number?: string; popStart?: string; popEnd?: string };
}
export interface DraftLike { id?: string; artifactId: string; skillId: string; skillVersion?: string; ranAt: Iso; summary: string; cites?: string[]; read?: string; agency?: string; unitId?: string | undefined; ownerUpn?: string | undefined; audience?: string | undefined }
export interface UnitLike {
  id: string; kind: "clin" | "unassigned" | "workstream" | "team"; name: string; parentId: string | null; active: boolean;
  /** A CLIN's line number. `ensureProgramUnits` writes it here and into `name`. */
  key?: string;
}
export interface UnitFinding { kind: string; cadenceId: string; unitId: string }
/**
 * One unit-scoped rule across the tree, counted two ways, because "12 missed" means two different
 * things here and a reader comparing this panel with the portfolio needs both.
 *
 * `eligible`, `ran`, `missed`, `withdrawn` and `unfilled` count **units**, on their latest period:
 * how the tree stands today. `periodsRan`, `periodsMissed` and `periodsWithdrawn` count **periods**,
 * the same periods the portfolio counts and by the same rule.
 *
 * Over the whole record, though, because every count on this page is: a cadence line beside this
 * panel reaches back over the record too, and windowing one panel of the page and not the rest would
 * be the divergence this fixed, one surface further in. The portfolio windows its own figure and
 * says so on its type, so the two are equal where a contract's cadence history is inside that window
 * and this page is the larger of the two where it is not.
 */
export interface UnitCadenceCount {
  cadenceId: string; name: string; eligible: number; ran: number; missed: number; withdrawn: number; unfilled: number;
  periodsRan: number; periodsMissed: number; periodsWithdrawn: number;
  /**
   * How many of those periods no unit's page can show, because the rows name no unit or name one the
   * rule no longer applies to. Counted here because the portfolio counts them and nothing else would:
   * without it the contract's lines plus this panel fall three short of the portfolio's figure on
   * COMET and a reader has no way to find out why.
   */
  periodsOffUnit: number;
  /**
   * An exception in force on this contract. Every count above it is zero, as on a contract line for
   * an excepted rule, and `periodsExcepted` is what the exception covers.
   */
  excepted: boolean;
  periodsExcepted: number;
}
/**
 * A unit somebody can open. Carries its parent and depth so the picker reads as the
 * tree it is: a flat list of thirty-two names tells a reader nothing about which one
 * they want (24 September).
 */
export interface UnitWeekLink {
  id: string; name: string; kind: "workstream" | "team";
  parentId: string | null;
  depth: number;
  people: number;
  /** Confirmed open work on this unit or anything under it. */
  openWork: number;
  /**
   * Items on this unit nobody has confirmed, declined or moved for longer than the
   * programme's `unattended-after`. Zero unless the reader leads the unit: it is work
   * on a contract, but only the person accountable for it is shown that it is sitting
   * (docs/2026-09-24-unconfirmed-work-is-visible.md).
   */
  unattendedCount: number;
  /** The same items, so a lead can see what was proposed, to whom and for how long. */
  unattendedItems: UnattendedLine[];
  missingDeliveryLead: boolean;
}
/** An item on a unit nobody has answered, for the lead accountable for that unit. */
export interface UnattendedLine {
  id: string;
  title: string;
  ownerUpn: PersonId;
  owner: string;
  /** The unit it is shown under: its own, or the one its owner sits on. */
  unitId: string;
  /** True when the item carries no unit of its own and was placed by its owner's membership. */
  viaOwner?: boolean;
  days: number;
  since: Iso;
}

/** One line per draft artifact on the program, for the "Drafts ready" list. */
export interface DraftLine { id?: string; artifactId: string; title: string; ranAt: Iso; cites: number; skillId: string }
/** A model gap raised from this program: the model was silent and the program needed it to speak. */
export interface GapLike { id: string; description: string; raisedByUpn: PersonId; raisedAt: Iso; status: string; modelVersion?: string }
/** One linked project, space, or team. Status is last poll, never a copied body. */
export interface LinkedLine {
  id: string;
  kind: string;
  key: string;
  /** What the far side calls itself, where it could be read. A key is a GUID for a team. */
  label?: string;
  instance: string;
  lastPoll?: Iso;
  count?: number;
  unreachable?: boolean;
  detail?: string;
  /** Read successfully and still doing nothing useful, and why. Never an outage. */
  note?: string;
}

export interface ProgramViewInput {
  viewer: PersonId;
  program: ProgramLike & { skillsEnabled?: boolean | undefined };
  roles: { roleId: string; upn: PersonId }[];
  roster: { upn: PersonId; flagged?: boolean; source?: string; company?: string | null; hoursInWindow?: number | null }[];
  people: { upn: PersonId; displayName: string; managerUpn?: string | null }[];
  commitments: Commitment[];
  events: Audit[];
  model: ModelLike;
  cadenceRuns?: CadenceRunLike[];
  contractEvents?: ContractEventLike[];
  drafts?: DraftLike[];
  gaps?: GapLike[];
  linked?: LinkedLine[];
  asOf?: Iso;
  units?: UnitLike[] | undefined;
  unitRoles?: { unitId: string; roleId: string; upn: PersonId }[] | undefined;
  unitMembers?: { unitId: string; upn: PersonId; until?: string | null }[] | undefined;
  optOuts?: { unitId: string; cadenceId: string }[] | undefined;
  unitFindings?: UnitFinding[] | undefined;
  /** Unit and ancestor role holders who may see intake on this view. */
  unitLeads?: PersonId[] | undefined;
  /** Initiatives this contract is attached to, or that its work already carries. */
  initiatives?: { id: string; name: string; endsOn?: Iso | null }[] | undefined;
  /** What each ask's source was, by `source.ref`, with identifiers already resolved to names. */
  askSources?: Record<string, AskSourceLike> | undefined;
  /** The contract's page in the contract system. Built by the caller, which owns the base URL. */
  contractUrl?: string | undefined;
}

// ------------------------------------------------------------------------------------------------ output

export type Tone = "good" | "watch" | "bad" | "neutral" | "off";

export interface RoleLine { roleId: string; name: string; holder?: { upn: PersonId; name: string }; gap: boolean }
export interface RosterLine {
  upn: PersonId; name: string; flagged: boolean; roleIds: string[];
  /** Where the row came from: corpdash, corpdash-stale, corpdash-hours, manual; absent for a role holder off the roster. */
  source?: string;
  /** "home" for the company's own people, else the subcontractor's mail domain. */
  company?: string;
  /** Hours billed to the program in the refresh window, when the row came from hours. */
  hours?: number;
}
export interface CadenceLine {
  cadenceId: string; name: string; frequency: string; runsBy: string; holder?: string;
  /** ran: evidence for a completed period; due: within a day; scheduled: open, not yet due; missed: a period ended without evidence. */
  status: "ran" | "due" | "scheduled" | "overdue" | "missed" | "not-measured" | "excepted";
  tone: Tone; detail: string; lastRan?: Iso; nextDue?: string;
  /**
   * Which periods these counts are over, because two surfaces show this same rule. `program` is the
   * contract's own runs; `unit` is one unit's, on a unit's page. Every count below reaches back over
   * the whole record, not over a window: the portfolio windows its own and says so on its type.
   */
  scope: "program" | "unit";
  missedCount: number;
  /**
   * Periods a program admin withdrew: the rule did not apply, or the contract had no configured way
   * to close them. Counted apart from `ranCount` and `missedCount` and absent from `completedCount`,
   * because a withdrawn period is not measured rather than passed or failed.
   */
  withdrawnCount: number;
  /**
   * Periods an exception in force took out of measurement. Zero unless `status` is `excepted`, where
   * it is every recorded period and `missedCount`, `ranCount` and `completedCount` are all zero: a
   * rule the contract excepted reports no verdict on any surface, and the count says how many
   * periods that silence covers rather than leaving them unaccounted for.
   */
  exceptedCount: number;
  /** Completed periods with evidence, and completed periods in all, for conformance. */
  ranCount: number; completedCount: number;
}
export interface AskLine {
  id: string; title: string; askedBy: string; channel?: string; askedAt: Iso; ageDays: number;
  /**
   * False when `askedAt` is when Trellis recorded the ask rather than when the customer made it,
   * because the ask cites no source Trellis can date. The age is then a floor, not a measurement.
   */
  askedAtStated?: boolean;
  /**
   * `waiting` is intake, nobody assigned. `assigned` is assigned and not yet accepted, which is the
   * state the eleven-day asks sat in: somebody's name is on it and nobody is watching it (issue #55).
   * `open` is accepted and on a record.
   */
  state: "waiting" | "assigned" | "open" | "closed"; owner?: string; ownerUpn?: string; dueDate: string; closedAt?: Iso;
  acknowledged: boolean; ackWithinStandard: boolean; link?: { system: string; ref: string };
  /**
   * Where the ticket or page behind `link` opens, when the source that created the ask carried one.
   * Not a redaction-gated fact: `link.system`/`link.ref` are already shown to anybody who can see
   * this line at all (see `detail`), and this is the same ticket at the same address, just clickable.
   */
  url?: string;
  tone: Tone; detail: string;
  /** On no unit: a contract fact nobody has classified yet, and not any one unit's work. */
  onNoUnit: boolean;
  /** Open or assigned, past its due date. `closed` and `waiting` are never overdue: a waiting ask has nobody's date on it yet. */
  overdue: boolean;
  /** How many days overdue, present only when `overdue` is true. */
  overdueDays?: number;
  /** How long the customer has waited to be acknowledged, and the standard that measures it. */
  waited: string; ackStandard: string;
  /** Where it came from, as a person reads it: "Email to Pinal Shah", "Teams message in COMET". */
  from: string;
  /** The asker's role, only where the customer register names their address exactly. */
  askedByRole?: string;
  /** Topic phrases this contract configured that the ask's source matched. */
  topics?: string[];
}
export interface RiskLine {
  id: string; title: string; detail: string; tone: Tone;
  kind: "overdue" | "roles-combined" | "role-unfilled" | "cadence-missed" | "asks-unacknowledged" | "roster-stale" | "contract-unverified" | "unit-lead-unfilled" | "unit-off-line" | "link-unreachable";
  /** Where a reader goes to settle it, when a route settles it. A path in this app, never an external link. */
  action?: { label: string; path: string };
}

/**
 * Open work and staffing on one contract line. A CLIN decides whether work is paid for, so
 * the workstreams sitting on no line are a row of their own: nobody can say whether that
 * work is covered, which is a finding rather than a blank.
 */
export interface ContractLineLoad {
  /** The CLIN unit's id, or "off-line" for the workstreams on the holding unit. */
  id: string;
  /** The CLIN number as the tree holds it, e.g. "0001". Empty on the off-line row. */
  key: string;
  name: string;
  onLine: boolean;
  workstreams: number;
  openWork: number;
  people: number;
}

/**
 * What the record did over a window, as counts. Deliberately not a series: there are days
 * of history, and six points drawn as a line claim a confidence the data does not have.
 */
export interface WindowCounts { days: number; opened: number; closed: number; closedOnDate: number }

/** The contract as the contract system holds it. Empty until CorpDash has been polled. */
/**
 * `url` is the contract's own page in the contract system, not a row's: the seam returns a link
 * per contract and none per modification or option period, so a reader following an unverified
 * event lands on the contract and finds the row there.
 */
export interface ContractFacts { corpdashId?: string; number?: string; popStart?: string; popEnd?: string; url?: string }

/** A quarterly push carrying work on this contract. One initiative may cross several. */
export interface InitiativeOnContract { id: string; name: string; open: number; total: number; endsOn?: Iso }
export interface Fraction { label: string; n?: number; of?: number; measured: boolean; text: string; tone: Tone }
export interface ContractEventLine { id: string; type: string; date?: Iso; state: string; title: string; tone: Tone }
export interface DraftCard { artifactId: string; skillId: string; skillVersion?: string; ranAt: Iso; summary: string; cites: string[]; read?: string; agency: string }
export interface GapLine { id: string; description: string; raisedBy: string; raisedAt: Iso; status: string }

export interface ProgramView {
  programId: ProgramId;
  viewer: PersonId;
  asOf: Iso;
  header: { name: string; profile: string; profileName: string; peopleCount: number; modelVersion: string; conformancePercent?: number };
  headline: { label: string; sentence: string };
  roles: RoleLine[];
  rosterLines: RosterLine[];
  customerRegister: RegisterEntryLike[];
  cadences: CadenceLine[];
  asks: AskLine[];
  /**
   * How many of `unacknowledged.count` the viewer may not open. Part of that same disclosure and
   * never wider: it lets the panel say what it is not showing without naming anything on it.
   */
  asksWithheld: number;
  /** The one thing this page may know about unconfirmed material: how many and how old. Never who. */
  unacknowledged: { count: number; oldestDays: number; pastStandard: number; standard: string };
  risks: RiskLine[];
  contractEvents: ContractEventLine[];
  drafts: DraftLine[];
  draft?: DraftCard;
  conformance: Fraction[];
  openCount: number;
  /** Open confirmed work past its date. */
  overdueCount: number;
  /** Open work per contract line, and the workstreams on none. Counts, never a rank. */
  contractLines: ContractLineLoad[];
  /** The record over the last ninety days, as counts and a window. */
  window: WindowCounts;
  /** The contract number and period of performance, when a contract event carries them. */
  contract: ContractFacts;
  /** Initiatives carrying work on this contract, name order. Counts, never a rank. */
  initiatives: InitiativeOnContract[];
  /** Open model gaps raised from this program, newest first. */
  gaps: GapLine[];
  /** Linked projects, spaces, and the program Team. Read, never copied. */
  linked: LinkedLine[];
  /** Whether skills may read this program's sources. The sponsor's call, per program. */
  skillsEnabled: boolean;
  /** The program's own parameter values, by id. Anything not here takes the model's default. */
  parameters: Record<string, unknown>;
  /** Unit-scoped cadences as counts across the tree, never a list of every standup. */
  unitCadences: UnitCadenceCount[];
  /** Workstreams and teams, name order, so This week can drill in. Not a rank. */
  unitsForWeek: UnitWeekLink[];
}

export interface UnitView extends ProgramView {
  unit: { id: string; name: string; kind: UnitLike["kind"] };
  /** The contract this unit is on. `header.name` is the unit's own name on this view. */
  programName: string;
  /**
   * The contract line that funds it, from the CLIN ancestor. Absent when the branch hangs
   * off the holding unit, which is a finding: nobody can say whether that work is covered.
   */
  line?: { id: string; key: string; name: string };
  /** Where it sits, outermost first, so the page can say so rather than leaving a reader to guess. */
  path: { id: string; name: string; kind: UnitLike["kind"] }[];
  /** Teams directly under it. A team may hold teams, so a unit page is not always a leaf. */
  children: { id: string; name: string; people: number }[];
  members: { upn: PersonId; name: string }[];
  ruleVersion: string;
  sources: string[];
  canSend: false;
}

export type UnitViewResult = { forbidden: true; reason: string } | { missing: true; reason: string } | { forbidden: false; view: UnitView };

export type ProgramViewResult = { forbidden: true; reason: string } | { forbidden: false; view: ProgramView };

// ------------------------------------------------------------------------------------------------ derivation

/** Access equivalent to the delivery lead. `program-admin` is not accountable; many people may hold it. */
export const PROGRAM_ADMIN_ROLE = "program-admin";
export const LEAD_ROLES = ["delivery-lead", "deputy-delivery-lead", PROGRAM_ADMIN_ROLE];
/** Unit roles that carry accountability for the work on that unit. A unit can hold
    other roles; holding one of those is not authority over somebody's record. */
export const UNIT_LEAD_ROLES = ["delivery-lead", "technical-lead"];
const EXTRA_ROLE_NAMES: Record<string, string> = { "portfolio-lead": "Portfolio lead", [PROGRAM_ADMIN_ROLE]: "Program admin" };

const CHANNEL_WORDS: Record<SourceKind, string> = {
  mail: "Email", chat: "Teams message", meeting: "Meeting", ticket: "Ticket", page: "Page",
  contract: "Contract record", model: "The delivery model", proposal: "Proposal",
  person: "Added by hand", manager: "From a manager",
};

/**
 * How much of an ask's source this viewer may be told, straight off the redaction the visibility
 * rule returns:
 *
 *  - `owner`: it is their own mailbox, calendar or chat, so the subject line or title is theirs.
 *  - `counterparty`: the `intake` redaction, which is "the ask, when, from whom per the register,
 *    via which channel". A lead is told which channel carried it and who was on it, never the
 *    subject or the transcript.
 *  - `channel`: every other redaction. `record` and `citation-only` give a non-owner the citation's
 *    kind and time and nothing around it, so a confirmed ask says only what kind of thing it came
 *    from, even to the lead who may read the ask itself.
 */
export type AskSourceDetail = "owner" | "counterparty" | "channel";

/**
 * Where an ask came from, the way somebody would say it: "Email to Pinal Shah", "Teams message in
 * COMET", or just "Email" where the rule allows no more.
 *
 * Nothing unresolved is printed. A team nobody has a name for reads as "Teams message", because a
 * GUID on the row is worse than a shorter sentence.
 */
export function askSourceLine(kind: SourceKind, detail: AskSourceDetail, src?: AskSourceLike): string {
  const k = src?.kind ?? kind;
  const word = CHANNEL_WORDS[k] ?? "Source";
  if (detail === "channel") return word;
  const where = k === "mail" && src?.to
    ? ` to ${src.to}${src.alsoTo ? ` and ${plural(src.alsoTo, "other")}` : ""}`
    : src?.team ? ` in ${src.team}` : "";
  const title = detail === "owner" && src?.title ? `: ${src.title}` : "";
  return `${word}${where}${title}`;
}

/** What of an ask's source this viewer may be told. An intake ask is a programme fact by construction. */
export function askSourceDetailFor(c: Commitment, viewer: PersonId, src?: AskSourceLike): AskSourceDetail {
  if (src?.ownerUpn && src.ownerUpn === viewer) return "owner";
  return c.status === "intake" ? "counterparty" : "channel";
}

/** The customer register as the program row holds it, with anything malformed dropped. */
export function registerOf(program: ProgramLike): RegisterEntryLike[] {
  return Array.isArray(program.customerRegister)
    ? (program.customerRegister as unknown[]).filter((e): e is RegisterEntryLike => !!e && typeof e === "object" && typeof (e as RegisterEntryLike).name === "string")
    : [];
}

/** How long the customer has waited to hear back, and whether that has passed the standard. */
export function askWaitLine(clock: { acknowledged: boolean; ms: number; pastStandard: boolean }, standard: string): string {
  const waited = fmtDuration(clock.ms);
  if (clock.acknowledged) return `acknowledged in ${waited}${clock.pastStandard ? ` · past the ${standard} standard` : ""}`;
  return `not acknowledged after ${waited} · ${clock.pastStandard ? `past the ${standard} standard` : `standard is ${standard}`}`;
}

/**
 * The asker's role, only where the register names their address exactly. A register entry for a
 * whole customer domain excludes contractors by display name and a stored source carries a name
 * for its sender at best, so reading a role off a domain entry would put the customer's title on
 * somebody who works for a competitor (docs/2026-09-25-attention-ranking.md).
 */
export function askedByRoleOf(address: string | undefined, register: RegisterEntryLike[]): string | undefined {
  if (!address) return undefined;
  const a = address.trim().toLowerCase();
  return register.find((e) => e.address.trim().toLowerCase() === a)?.role || undefined;
}

/**
 * Customer asks as lines: who asked and in what role, where it came from, how long it has waited
 * against the acknowledgement standard, and whether anybody has put it on a unit. The caller decides
 * which asks it may show; the visibility rule is not applied again here.
 *
 * Shared so the contract page and Structure say the same thing about the same ask.
 */
export function askLines(input: {
  asks: Commitment[];
  viewer: PersonId;
  people: { upn: PersonId; displayName: string }[];
  program: ProgramLike;
  model: ModelLike;
  events?: Audit[] | undefined;
  askSources?: Record<string, AskSourceLike> | undefined;
  asOf?: Iso | undefined;
}): AskLine[] {
  const asOf = input.asOf ?? new Date().toISOString();
  const today = dateOf(asOf);
  const names = new Map(input.people.map((p) => [p.upn, p.displayName]));
  const nameOf = (upn: PersonId | undefined) => (upn ? names.get(upn) ?? upn : "");
  const ackMs = parseDuration(String(paramReader(input.model, input.program)("ack-within") ?? "P1D"));
  const ackStandard = fmtDuration(ackMs);
  const register = registerOf(input.program);
  const eventsBy = groupEvents(input.events ?? []);
  return input.asks.map((c): AskLine => {
    const clock = ackClock(c, asOf, ackMs);
    const closedAt = c.status === "closed" ? lastEventAt(eventsBy.get(c.id), "closed") ?? c.updatedAt : undefined;
    const state: AskLine["state"] = c.status === "intake" ? "waiting" : c.status === "closed" ? "closed" : c.status === "candidate" ? "assigned" : "open";
    const overdue = (state === "open" || state === "assigned") && c.dueDate < today;
    const tone: Tone = clock.pastStandard && !clock.acknowledged ? "bad" : overdue ? "bad" : state === "waiting" || state === "assigned" ? "watch" : clock.pastStandard ? "watch" : "good";
    const who = c.askedBy?.name ?? "Customer";
    const role = askedByRoleOf(c.askedBy?.address, register);
    const src = input.askSources?.[c.source.ref];
    const detail = askSourceDetailFor(c, input.viewer, src);
    const from = askSourceLine(c.source.kind, detail, src);
    // "asked on" is a claim about what the customer did. Where the source never stated a date, the
    // line shows the closest one the record holds and says the source did not state it.
    const askedWords = c.source.atStated === false ? `${fmtDate(c.source.at)}, date not stated by the source` : `asked ${fmtDate(c.source.at)}`;
    const parts = [`${who}${role ? `, ${role}` : ""} · ${askedWords}`];
    const dueWords = `due ${fmtDate(c.dueDate)}${overdue ? ` (${plural(daysBetween(c.dueDate, today), "day")} overdue)` : ""}`;
    if (state === "waiting") parts.push(`waiting for an owner · held by ${nameOf(c.owner)}`);
    else if (state === "assigned") parts.push(`assigned to ${nameOf(c.owner)}, not yet accepted · ${dueWords}`);
    else if (state === "open") parts.push(`${nameOf(c.owner)}, ${dueWords}`);
    else parts.push(`${nameOf(c.owner)} · closed ${fmtDay(closedAt!)}`);
    if (c.link) parts.push(`${c.link.system} ${c.link.ref}`);
    parts.push(askWaitLine(clock, ackStandard));
    return {
      id: c.id, title: c.title, askedBy: who, ...(role ? { askedByRole: role } : {}),
      ...(c.askedBy?.channel ? { channel: c.askedBy.channel } : {}),
      askedAt: c.source.at, ageDays: daysBetween(c.source.at, asOf), state,
      ...(c.source.atStated === false ? { askedAtStated: false } : {}),
      owner: nameOf(c.owner), ownerUpn: c.owner, dueDate: c.dueDate, ...(closedAt ? { closedAt } : {}),
      acknowledged: clock.acknowledged, ackWithinStandard: !clock.pastStandard,
      ...(c.link ? { link: c.link } : {}), ...(c.link && c.source.url ? { url: c.source.url } : {}),
      tone, detail: parts.join(" · "),
      onNoUnit: !c.unitId, overdue, ...(overdue ? { overdueDays: daysBetween(c.dueDate, today) } : {}),
      waited: fmtDuration(clock.ms), ackStandard, from,
      ...(detail !== "channel" && src?.topics?.length ? { topics: src.topics } : {}),
    };
  }).sort((a, b) => Number(b.state !== "closed") - Number(a.state !== "closed") || b.askedAt.localeCompare(a.askedAt));
}

export function programViewFor(input: ProgramViewInput): ProgramViewResult {
  const holders = new Set(input.roles.map((r) => r.upn));
  if (!holders.has(input.viewer)) return { forbidden: true, reason: "the program view is for the people who hold a role on the program" };
  return { forbidden: false, view: deriveProgramView(input) };
}

export function deriveProgramView(input: ProgramViewInput): ProgramView {
  const asOf = input.asOf ?? new Date().toISOString();
  const today = dateOf(asOf);
  const names = new Map(input.people.map((p) => [p.upn, p.displayName]));
  const nameOf = (upn: PersonId | undefined) => (upn ? names.get(upn) ?? upn : "");
  const params = paramReader(input.model, input.program);
  const ackMs = parseDuration(String(params("ack-within") ?? "P1D"));
  const ackStandard = fmtDuration(ackMs);
  const profile = input.model.profiles.find((p) => p.id === input.program.profile);
  const eventsBy = groupEvents(input.events);

  // Roles: every one the model names, then any the program added on top (a program type may add roles).
  const holderOf = new Map(input.roles.map((r) => [r.roleId, r.upn]));
  const roles: RoleLine[] = input.model.roles.map((r) => {
    const upn = holderOf.get(r.id);
    return { roleId: r.id, name: r.name, gap: !upn, ...(upn ? { holder: { upn, name: nameOf(upn) } } : {}) };
  });
  for (const r of input.roles) if (!input.model.roles.some((m) => m.id === r.roleId)) roles.push({ roleId: r.roleId, name: EXTRA_ROLE_NAMES[r.roleId] ?? r.roleId, gap: false, holder: { upn: r.upn, name: nameOf(r.upn) } });

  const rosterUpns = new Set([...input.roster.map((r) => r.upn), ...input.roles.map((r) => r.upn)]);
  const rosterLines: RosterLine[] = [...rosterUpns].map((upn) => {
    const row = input.roster.find((r) => r.upn === upn);
    return {
      upn, name: nameOf(upn), flagged: row?.flagged ?? !row,
      roleIds: input.roles.filter((r) => r.upn === upn).map((r) => r.roleId),
      ...(row?.source ? { source: row.source } : {}),
      ...(row?.company ? { company: row.company } : {}),
      ...(typeof row?.hoursInWindow === "number" ? { hours: row.hoursInWindow } : {}),
    };
  }).sort((a, b) => b.roleIds.length - a.roleIds.length || a.name.localeCompare(b.name));

  const customerRegister = registerOf(input.program);

  // The visibility rule decides what of each commitment this page may use. Candidates are "none" for
  // everyone but their owner, and this page ignores even the owner's own (see the module comment).
  const roster: Roster = {
    programId: input.program.id, members: [...rosterUpns],
    managerOf: Object.fromEntries(input.people.map((p) => [p.upn, p.managerUpn ?? undefined])),
    leads: [...input.roles.filter((r) => LEAD_ROLES.includes(r.roleId)).map((r) => r.upn), ...(input.unitLeads ?? [])],
  };
  // Confirmed work only. This list feeds the customer-facing asks section and the
  // cadence evidence, where a candidate has no business appearing however visible it
  // has become to a lead; the unit view surfaces those separately.
  const confirmed = input.commitments.filter((c) => c.status !== "candidate" && visibility(c, { kind: "person", id: input.viewer }, roster) !== "none");
  const open = confirmed.filter((c) => c.status === "open");

  // Cadences: the profile's program-scoped ones. Unit-scoped cadences are counts on This week, lines on a unit.
  const cadences: CadenceLine[] = cadenceLines({
    ids: (profile?.cadences ?? []).filter((id) => input.model.cadences.find((c) => c.id === id)?.scope !== "unit"),
    model: input.model, confirmed, cadenceRuns: input.cadenceRuns ?? [], exceptions: input.program.instance?.exceptions ?? [],
    today, eventsBy, params, holderOf, nameOf, scope: "program",
  });

  // Customer asks: intake (a program fact for the lead roles), assigned and not yet accepted, and
  // confirmed ones. An assigned ask used to drop off this panel until its assignee accepted, so the
  // lead lost sight of the one item nobody was watching, which is what the eleven-day asks were
  // (issue #55). Visibility still decides who may see it, and a refused or set-aside ask is off the
  // panel because it is no longer waiting on anybody.
  const askCommitments = input.commitments.filter((c) => c.kind === "ask" && c.status !== "declined" && c.status !== "set-aside"
    && visibility(c, { kind: "person", id: input.viewer }, roster) !== "none");
  const asks = askLines({ asks: askCommitments, viewer: input.viewer, people: input.people, program: input.program, model: input.model, events: input.events, asOf, ...(input.askSources ? { askSources: input.askSources } : {}) });

  // Count and age of unacknowledged asks over everything, confirmed or not. The one permitted leak.
  const waiting = input.commitments.filter((c) => c.kind === "ask" && !c.acknowledgedAt && LIVE_STATUSES.includes(c.status));
  // How many of that count the panel cannot list. Drawn from `waiting` and from nothing wider:
  // the exception in the vision is the unacknowledged count and age, so a reader already told
  // thirty-five are unacknowledged learns nothing new from being told they are all withheld.
  // Counting every hidden ask instead, closed and acknowledged ones included, would widen the
  // exception (docs/2026-09-20-program-structure.md, "Roll-ups").
  const asksWithheld = waiting.filter((c) => visibility(c, { kind: "person", id: input.viewer }, roster) === "none").length;
  const unacknowledged = {
    count: waiting.length,
    oldestDays: waiting.reduce((m, c) => Math.max(m, daysBetween(c.source.at, asOf)), 0),
    pastStandard: waiting.filter((c) => ackClock(c, asOf, ackMs).pastStandard).length,
    standard: ackStandard,
  };

  // Risks, from the record and the tables. Not from inboxes.
  const unitLinks = unitsForWeek(input);
  const structurePath = `/app/programs/${encodeURIComponent(input.program.id)}/structure`;
  const risks: RiskLine[] = [];
  const overdue = open.filter((c) => c.dueDate < today).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  for (const c of overdue.slice(0, 5)) risks.push({ id: `overdue:${c.id}`, kind: "overdue", tone: "bad", title: `${c.title} is ${plural(daysBetween(c.dueDate, today), "day")} overdue`, detail: `${nameOf(c.owner)} · due ${fmtDateFull(c.dueDate)}${c.originalDueDate && c.originalDueDate !== c.dueDate ? ` · first accepted ${fmtDayFull(c.originalDueDate)}` : ""}` });
  if (overdue.length > 5) risks.push({ id: "overdue:more", kind: "overdue", tone: "bad", title: `${overdue.length - 5} more overdue`, detail: "From the record" });
  const byHolder = new Map<PersonId, string[]>();
  for (const r of input.roles) byHolder.set(r.upn, [...(byHolder.get(r.upn) ?? []), r.roleId]);
  for (const [upn, held] of byHolder) if (held.length >= 2) risks.push({ id: `roles:${upn}`, kind: "roles-combined", tone: held.length >= 3 ? "watch" : "neutral", title: `One person holds ${held.length} of ${roles.length} roles`, detail: `${nameOf(upn)}: ${held.map((h) => roles.find((r) => r.roleId === h)?.name ?? h).join(", ")}` });
  for (const r of roles.filter((x) => x.gap)) risks.push({ id: `gap:${r.roleId}`, kind: "role-unfilled", tone: LEAD_ROLES.includes(r.roleId) ? "bad" : "watch", title: `Nobody holds ${r.name.toLowerCase()}`, detail: "Hard rule 1: one named person per role. The cadences this role runs have no owner until it is filled" });
  const leaderless = unitLinks.filter((u) => u.missingDeliveryLead);
  if (leaderless.length > 0) risks.push({
    id: "units:no-delivery-lead", kind: "unit-lead-unfilled", tone: "watch",
    title: `${plural(leaderless.length, "unit")} ${leaderless.length === 1 ? "has" : "have"} no delivery lead`,
    detail: `${plural(leaderless.reduce((n, u) => n + u.people, 0), "person", "people")} between them · ${leaderless.slice(0, 3).map((u) => u.name).join(", ")}${leaderless.length > 3 ? `, and ${leaderless.length - 3} more` : ""}`,
    action: { label: "Name one", path: structurePath },
  });
  const offLine = workstreamsOffLine(input.units ?? []);
  if (offLine.length > 0) risks.push({
    id: "units:no-clin", kind: "unit-off-line", tone: "watch",
    title: `${plural(offLine.length, "workstream")} ${offLine.length === 1 ? "sits" : "sit"} on no contract line`,
    detail: `${offLine.map((u) => u.name).join(", ")} · nobody can say whether that work is funded`,
    action: { label: "Put it on a line", path: structurePath },
  });
  for (const l of (input.linked ?? []).filter((x) => x.unreachable)) risks.push({
    id: `linked:${l.id}`, kind: "link-unreachable", tone: "bad",
    title: `${l.instance || l.kind} ${l.key} could not be read`,
    detail: [l.detail, l.lastPoll ? `last read ${fmtStamp(l.lastPoll, "UTC")}` : "never read"].filter(Boolean).join(" · "),
    action: { label: "Open Instances", path: "/app/integrations" },
  });
  for (const c of cadences.filter((x) => x.status === "missed" && missedIsEscalation(input.model, x.cadenceId))) risks.push({ id: `cadence:${c.cadenceId}`, kind: "cadence-missed", tone: c.tone, title: `${c.name} did not run`, detail: c.detail });
  if (unacknowledged.pastStandard > 0) risks.push({ id: "asks:unacknowledged", kind: "asks-unacknowledged", tone: "bad", title: `${plural(unacknowledged.pastStandard, "customer ask")} unacknowledged past the standard`, detail: `Oldest ${plural(unacknowledged.oldestDays, "day")} · standard is ${ackStandard} · count and age only` });
  const stale = rosterLines.filter((r) => r.flagged).length;
  if (stale > 0) risks.push({ id: "roster:stale", kind: "roster-stale", tone: "neutral", title: `Roster is ${stale === rosterLines.length ? "entirely" : `${stale} of ${rosterLines.length}`} from a stale staffing window`, detail: "CorpDash has no staffing after the window shown; rows are flagged until it does" });
  // Unverified is unverified: the whole record, newest first, with a display cap. This used to
  // be narrowed to the last 90 days while the portfolio narrowed it the same way and the record
  // table below narrowed it not at all, so three surfaces gave three answers to one question.
  const unverified = (input.contractEvents ?? []).filter((x) => x.state === "unverified").sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
  for (const e of unverified.slice(0, 5)) risks.push({ id: `contract:${e.id}`, kind: "contract-unverified", tone: "watch", title: `${e.title ?? cap(e.type.replace(/-/g, " "))}${e.date ? ` on ${fmtDayFull(e.date)}` : ""} is unverified`, detail: "Recorded from CorpDash; not yet confirmed against the signed document" });
  if (unverified.length > 5) risks.push({ id: "contract:more", kind: "contract-unverified", tone: "watch", title: `${unverified.length - 5} more contract events are unverified`, detail: "In the contract record below" });

  const contractEvents: ContractEventLine[] = (input.contractEvents ?? []).map((e): ContractEventLine => ({ id: e.id, type: e.type, ...(e.date ? { date: e.date } : {}), state: e.state, title: e.title ?? cap(e.type.replace(/-/g, " ")), tone: e.state === "confirmed" ? "good" : e.state === "unverified" ? "watch" : "neutral" })).sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));

  const visibleDrafts = (input.drafts ?? []).filter((d) => !isHiddenDraft(d));
  const draftRow = visibleDrafts.filter((d) => d.artifactId === "customer-status-report" || d.artifactId === "weekly-status").sort((a, b) => b.ranAt.localeCompare(a.ranAt))[0];
  const drafts: DraftLine[] = visibleDrafts.map((d) => ({ ...(d.id ? { id: d.id } : {}), artifactId: d.artifactId, title: d.summary, ranAt: d.ranAt, cites: d.cites?.length ?? 0, skillId: d.skillId })).sort((a, b) => b.ranAt.localeCompare(a.ranAt));
  const draft: DraftCard | undefined = draftRow ? { artifactId: draftRow.artifactId, skillId: draftRow.skillId, ...(draftRow.skillVersion ? { skillVersion: draftRow.skillVersion } : {}), ranAt: draftRow.ranAt, summary: draftRow.summary, cites: draftRow.cites ?? [], ...(draftRow.read ? { read: draftRow.read } : {}), agency: draftRow.agency ?? "draft" } : undefined;

  // Conformance: fractions over denominators the model defines; "not measured" where it has not.
  const filled = roles.filter((r) => !r.gap).length;
  const recentAsks = askCommitments.filter((c) => daysBetween(c.source.at, asOf) <= 30);
  const inStandard = recentAsks.filter((c) => !ackClock(c, asOf, ackMs).pastStandard).length;
  const conformance: Fraction[] = [
    { label: "Roles", n: filled, of: roles.length, measured: true, text: `${filled} of ${roles.length} named`, tone: filled === roles.length ? "good" : "watch" },
    cadenceFraction(cadences),
    recentAsks.length
      ? { label: "Customer standard", n: inStandard, of: recentAsks.length, measured: true, text: inStandard === recentAsks.length ? "All asks in standard" : `${inStandard} of ${recentAsks.length} in standard`, tone: inStandard === recentAsks.length ? "good" : "bad" }
      // "No asks in 30 days" would be a claim about the contract made from what one reader may
      // see. With asks withheld it is a claim about the reader, and it says so.
      : { label: "Customer standard", measured: false, text: asksWithheld > 0 ? "Not measurable from what you may see" : "No asks in 30 days", tone: "off" },
    { label: "Knowledge", measured: false, text: "Not measured", tone: "off" },
    { label: "Gates", measured: false, text: "Not measured", tone: "off" },
  ];
  const conformancePercent = conformanceOver(conformance);

  const peopleCount = rosterLines.length;
  const sentence = headlineSentence({ peopleCount, filled, rolesTotal: roles.length, open: open.length, asksWaiting: asks.filter((a) => a.state !== "closed").length, overdue: overdue.length, pmMode: (input.program.profile || "").startsWith("pm-mode") });

  const gaps: GapLine[] = (input.gaps ?? []).filter((g) => g.status === "open").map((g) => ({ id: g.id, description: g.description, raisedBy: nameOf(g.raisedByUpn), raisedAt: g.raisedAt, status: g.status })).sort((a, b) => b.raisedAt.localeCompare(a.raisedAt));
  const parameters: Record<string, unknown> = { ...(input.program.instance?.parameters ?? {}) };

  return {
    programId: input.program.id, viewer: input.viewer, asOf,
    header: { name: input.program.name, profile: input.program.profile, profileName: profile?.name ?? input.program.profile, peopleCount, modelVersion: input.program.modelVersion || input.model.version, ...(conformancePercent === undefined ? {} : { conformancePercent }) },
    headline: { label: `This week · ${fmtDate(today)}`, sentence },
    roles, rosterLines, customerRegister, cadences, asks, asksWithheld, unacknowledged, risks, contractEvents, ...(draft ? { draft } : {}), drafts, conformance, openCount: open.length,
    overdueCount: overdue.length,
    contractLines: contractLineLoad(input.units ?? [], open, input.unitMembers ?? []),
    window: windowCounts(confirmed, input.events, asOf),
    contract: contractFacts(input.program, input.contractEvents ?? [], input.contractUrl),
    initiatives: initiativesOnContract(input.initiatives ?? [], confirmed),
    gaps, linked: input.linked ?? [], skillsEnabled: input.program.skillsEnabled ?? false, parameters,
    unitCadences: unitCadenceCounts(input, profile, confirmed, today),
    unitsForWeek: unitLinks,
  };
}

/**
 * The units a reader can open, in tree order: each workstream followed by its teams,
 * depth-first. Depth and parent travel with the row so the picker can indent rather
 * than flatten, and each row carries why somebody would click it.
 */
/** Whether the reader holds an accountable role on this unit or anything under it. */
function leadsUnit(input: ProgramViewInput, sub: Set<string>): boolean {
  if ((input.roles ?? []).some((r) => r.upn === input.viewer && LEAD_ROLES.includes(r.roleId))) return true;
  return (input.unitRoles ?? []).some((r) => r.upn === input.viewer && UNIT_LEAD_ROLES.includes(r.roleId) && sub.has(r.unitId));
}

export function unitsForWeek(input: ProgramViewInput): UnitWeekLink[] {
  const asOfDate = new Date(input.asOf ?? new Date().toISOString());
  // The programme's value, else the model's own default, else two days. Reading the
  // model matters: a default that moves should move the threshold with it.
  const afterMs = thresholdMs(paramReader(input.model, input.program)("unattended-after"));
  const peopleNames = new Map((input.people ?? []).map((p) => [p.upn, p.displayName]));
  const nameOf = (upn: PersonId) => peopleNames.get(upn) ?? upn;
  const all = (input.units ?? []).filter((u) => u.active && (u.kind === "workstream" || u.kind === "team"));
  const members = input.unitMembers ?? [];
  // A candidate carries no unit: `unit_id` is null until somebody confirms it. Attributing
  // unattended work by the commitment's own unit therefore found nothing at all, which is
  // exactly the work the surface exists for. Fall back to where its owner sits.
  const unitsOfPerson = new Map<PersonId, Set<string>>();
  for (const m of members) {
    if (m.until) continue;
    const set = unitsOfPerson.get(m.upn) ?? new Set<string>();
    set.add(m.unitId);
    unitsOfPerson.set(m.upn, set);
  }
  const onSubtree = (c: Commitment, sub: Set<string>): boolean => {
    if (c.unitId) return sub.has(c.unitId);
    const held = unitsOfPerson.get(c.owner);
    return held ? [...held].some((id) => sub.has(id)) : false;
  };
  const unitRoles = input.unitRoles ?? [];
  const byParent = new Map<string | null, UnitLike[]>();
  for (const u of all) byParent.set(u.parentId, [...(byParent.get(u.parentId) ?? []), u]);
  const kids = (id: string | null) => (byParent.get(id) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));

  const descendants = (id: string): string[] => [id, ...kids(id).flatMap((k) => descendants(k.id))];
  const out: UnitWeekLink[] = [];
  const walk = (u: UnitLike, depth: number) => {
    const sub = new Set(descendants(u.id));
    const waiting: UnattendedLine[] = leadsUnit(input, sub)
      ? unattended(input.commitments.filter((c) => onSubtree(c, sub)), input.events, asOfDate, afterMs)
          .map((w) => ({
            id: w.commitment.id, title: w.commitment.title, ownerUpn: w.commitment.owner,
            owner: nameOf(w.commitment.owner), unitId: w.commitment.unitId ?? u.id,
            ...(w.commitment.unitId ? {} : { viaOwner: true }),
            days: w.days, since: w.since,
          }))
      : [];
    out.push({
      id: u.id, name: u.name, kind: u.kind as "workstream" | "team", parentId: u.parentId, depth,
      // Across the subtree, because the page this links to counts the subtree and says
      // so. A parent whose people all sit on its teams would otherwise read as empty.
      people: new Set(members.filter((m) => sub.has(m.unitId) && !m.until).map((m) => m.upn)).size,
      openWork: input.commitments.filter((c) => c.unitId && sub.has(c.unitId) && c.status === "open").length,
      unattendedCount: waiting.length,
      unattendedItems: waiting,
      missingDeliveryLead: !unitRoles.some((r) => r.unitId === u.id && r.roleId === "delivery-lead"),
    });
    for (const k of kids(u.id)) walk(k, depth + 1);
  };
  // A workstream's parent is a CLIN or the holding unit, neither of which is in `all`.
  for (const w of all.filter((u) => u.kind === "workstream").sort((a, b) => a.name.localeCompare(b.name))) walk(w, 0);
  // A team whose workstream the reader cannot see still belongs somewhere.
  for (const t of all.filter((u) => u.kind === "team" && !out.some((o) => o.id === u.id))) walk(t, 0);
  return out;
}

/** Workstreams hanging off the holding unit rather than a CLIN, in name order. */
export function workstreamsOffLine(units: UnitLike[]): UnitLike[] {
  const holding = new Set(units.filter((u) => u.active && u.kind === "unassigned").map((u) => u.id));
  return units
    .filter((u) => u.active && u.kind === "workstream" && u.parentId !== null && holding.has(u.parentId))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Open work and the people on it, per contract line. The rows are the CLINs in number order,
 * then one row for everything on no line at all; it is left out when every workstream is on one.
 */
export function contractLineLoad(
  units: UnitLike[],
  open: Commitment[],
  members: { unitId: string; upn: PersonId; until?: string | null }[],
): ContractLineLoad[] {
  const live = units.filter((u) => u.active);
  const byParent = new Map<string | null, UnitLike[]>();
  for (const u of live) byParent.set(u.parentId, [...(byParent.get(u.parentId) ?? []), u]);
  const subtree = (id: string): Set<string> => {
    const out = new Set<string>();
    const walk = (x: string) => { out.add(x); for (const k of byParent.get(x) ?? []) walk(k.id); };
    walk(id);
    return out;
  };
  const load = (roots: UnitLike[], id: string, key: string, name: string, onLine: boolean): ContractLineLoad => {
    const ids = new Set(roots.flatMap((r) => [...subtree(r.id)]));
    return {
      id, key, name, onLine,
      workstreams: roots.filter((r) => r.kind === "workstream").length,
      openWork: open.filter((c) => c.unitId && ids.has(c.unitId)).length,
      people: new Set(members.filter((m) => !m.until && ids.has(m.unitId)).map((m) => m.upn)).size,
    };
  };
  const lines = live
    .filter((u) => u.kind === "clin")
    .map((clin) => ({ clin, label: clinLabel(clin) }))
    // Numeric, so an unpadded CorpDash key sorts 2 before 10 rather than after it.
    .sort((a, b) => a.label.key.localeCompare(b.label.key, undefined, { numeric: true }) || a.clin.name.localeCompare(b.clin.name))
    .map(({ clin, label }) => {
      // The CLIN itself carries people directly, so it is part of its own subtree.
      const row = load([clin, ...(byParent.get(clin.id) ?? [])], clin.id, label.key, label.name, true);
      return { ...row, workstreams: (byParent.get(clin.id) ?? []).filter((u) => u.kind === "workstream").length };
    });
  const offLine = workstreamsOffLine(live);
  if (offLine.length > 0) lines.push(load(offLine, "off-line", "", "On no contract line", false));
  return lines;
}

/**
 * A contract line's number and the name a lead gave it. `ensureProgramUnits` writes the
 * number into both `key` and `name`, so most CLINs are called "0001" and have no name of
 * their own; a lead who renames one writes "CLIN 0001 Platform operations". The number
 * comes off `key` wherever there is one, and the name is whatever is left over.
 */
export function clinLabel(u: { name: string; key?: string | undefined }): { key: string; name: string } {
  const key = (u.key ?? "").trim() || /^CLIN\s+(\S+)/i.exec(u.name)?.[1] || "";
  let name = u.name.trim().replace(/^CLIN\s+/i, "").trim();
  if (key && name.startsWith(key)) name = name.slice(key.length).trim();
  return { key: key || u.name.trim(), name };
}

/**
 * What the record did over a window: how much was opened, how much closed, and how much of that
 * closed on the date first accepted. The same rule `deriveRecord` uses, over a whole contract.
 * Counts and a window, never a series (REALISM: five days of events is not a trend).
 */
export function windowCounts(commitments: Commitment[], events: Audit[], asOf: Iso, days = 90): WindowCounts {
  const today = dateOf(asOf);
  const eventsBy = groupEvents(events);
  const inWindow = (date: string) => daysBetween(date, today) <= days && daysBetween(date, today) >= 0;
  let opened = 0;
  let closed = 0;
  let closedOnDate = 0;
  for (const c of commitments) {
    if (inWindow(dateOf(c.createdAt))) opened++;
    if (c.status !== "closed") continue;
    const evs = eventsBy.get(c.id);
    const closeDate = dateOf(lastEventAt(evs, "closed") ?? c.updatedAt);
    if (!inWindow(closeDate)) continue;
    closed++;
    const moves = (evs ?? []).filter((e) => e.event.type === "moved").length;
    if (moves === 0 && daysBetween(c.originalDueDate ?? c.dueDate, closeDate) <= 0) closedOnDate++;
  }
  return { days, opened, closed, closedOnDate };
}

/**
 * The contract number and period of performance, off the award row CorpDash last sent.
 * Nothing until the contract has been polled: the number is not a Trellis column.
 */
export function contractFacts(program: ProgramLike, events: ContractEventLike[], url?: string): ContractFacts {
  const award = events
    .filter((e) => e.type === "award" && e.state !== "superseded" && e.contract)
    .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))[0];
  const p = award?.contract ?? {};
  const text = (v: string | undefined) => (v && v.trim() ? v.trim() : undefined);
  return {
    ...(program.corpdashContractId ? { corpdashId: program.corpdashContractId } : {}),
    ...(program.corpdashContractId && url ? { url } : {}),
    ...(text(p.number) ? { number: text(p.number)! } : {}),
    ...(text(p.popStart) ? { popStart: text(p.popStart)! } : {}),
    ...(text(p.popEnd) ? { popEnd: text(p.popEnd)! } : {}),
  };
}

/**
 * Initiatives with work on this contract, counted off the commitments that carry the label.
 * An initiative nobody has labelled work under still appears: it is attached, at zero.
 */
export function initiativesOnContract(
  initiatives: { id: string; name: string; endsOn?: Iso | null }[],
  confirmed: Commitment[],
): InitiativeOnContract[] {
  return initiatives
    .map((i) => {
      const mine = confirmed.filter((c) => c.initiativeId === i.id);
      return { id: i.id, name: i.name, open: mine.filter((c) => c.status === "open").length, total: mine.length, ...(i.endsOn ? { endsOn: i.endsOn } : {}) };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function unitViewFor(input: ProgramViewInput & { unitId: string }): UnitViewResult {
  const unit = (input.units ?? []).find((u) => u.id === input.unitId);
  if (!unit) return { missing: true, reason: "no such unit" };
  if (!maySeeUnitView(input.viewer, unit.id, {
    programRoles: input.roles,
    unitRoles: input.unitRoles ?? [],
    units: input.units ?? [],
  })) return { forbidden: true, reason: "this week's view on a unit is for that unit's role holders, their ancestors, and the program's role holders" };
  return { forbidden: false, view: deriveUnitView({ ...input, unitId: unit.id }) };
}

export function deriveUnitView(input: ProgramViewInput & { unitId: string }): UnitView {
  const unit = (input.units ?? []).find((u) => u.id === input.unitId);
  if (!unit) throw new Error("unit is missing");
  const subtree = subtreeIds(input.units ?? [], unit.id);
  const names = new Map(input.people.map((p) => [p.upn, p.displayName]));
  const nameOf = (upn: PersonId | undefined) => (upn ? names.get(upn) ?? upn : "");
  const opted = new Set((input.optOuts ?? []).filter((o) => o.unitId === unit.id).map((o) => o.cadenceId));
  const programLeads = new Set(input.roles.filter((r) => LEAD_ROLES.includes(r.roleId)).map((r) => r.upn));
  const ancestors = ancestorIds(input.units ?? [], unit.id);
  const managerOf = new Map(input.people.map((p) => [p.upn, p.managerUpn ?? undefined]));
  const drafts = (input.drafts ?? []).filter((d) => draftVisibleOnUnit(d, {
    viewer: input.viewer, subtree, ancestors, programLeads, managerOf,
    unitRoles: input.unitRoles ?? [], artifacts: input.model.artifacts ?? [],
  }));
  const scopedRuns = (input.cadenceRuns ?? []).filter((r) => r.unitId === unit.id);
  // Accountable roles only. A unit can hold a customer contact; that is not authority
  // over the work on it, and `unitLeads` is what grants sight of unconfirmed items.
  const unitLeads = (input.unitRoles ?? [])
    .filter((r) => UNIT_LEAD_ROLES.includes(r.roleId) && (subtree.has(r.unitId) || ancestors.includes(r.unitId)))
    .map((r) => r.upn);
  // An ask on no unit is a contract fact, not this unit's. It used to be added here for anybody
  // holding a programme role, which is nothing about the unit being read, so the same list appeared
  // on every unit page and read as that unit's work. It lives on the contract page, and Structure is
  // where it gets placed.
  const base = deriveProgramView({
    ...input,
    commitments: input.commitments.filter((c) => c.unitId && subtree.has(c.unitId)),
    cadenceRuns: scopedRuns,
    drafts,
    contractEvents: [],
    linked: [],
    gaps: [],
    unitLeads,
  });
  const profile = input.model.profiles.find((p) => p.id === input.program.profile);
  const eligibleIds = (profile?.cadences ?? []).filter((id) => {
    const def = input.model.cadences.find((c) => c.id === id);
    return !!def && def.scope === "unit" && (def.eligibleKinds ?? []).includes(unit.kind as "workstream" | "team") && !opted.has(id);
  });
  const holderOf = new Map<string, PersonId>();
  const unitRoles = input.unitRoles ?? [];
  for (const r of unitRoles.filter((x) => x.unitId === unit.id)) holderOf.set(r.roleId, r.upn);
  if (!holderOf.has("technical-lead") && holderOf.has("delivery-lead")) holderOf.set("technical-lead", holderOf.get("delivery-lead")!);
  const today = dateOf(base.asOf);
  const params = paramReader(input.model, input.program);
  const eventsBy = groupEvents(input.events);
  // A lead of this unit sees what is waiting on it as well as what is confirmed; the
  // rest of the roster sees confirmed work only (24 September).
  const leadsHere = unitLeads.includes(input.viewer) || (input.roles ?? []).some((r) => r.upn === input.viewer && LEAD_ROLES.includes(r.roleId));
  // The visibility rule decides what this page may use, as it does on the contract's. This page
  // applied none of it: it read every commitment on the unit whoever was looking, so a reader the
  // rule gives nothing could be shown an item the same rule hides one route away.
  const unitRoster: Roster = {
    programId: input.program.id,
    members: [...new Set([...input.roster.map((r) => r.upn), ...input.roles.map((r) => r.upn)])],
    managerOf: Object.fromEntries(input.people.map((p) => [p.upn, p.managerUpn ?? undefined])),
    leads: [...input.roles.filter((r) => LEAD_ROLES.includes(r.roleId)).map((r) => r.upn), ...unitLeads],
  };
  const confirmed = input.commitments.filter((c) => c.unitId === unit.id
    && (c.status !== "candidate" || leadsHere)
    && visibility(c, { kind: "person", id: input.viewer }, unitRoster) !== "none");
  const cadences = cadenceLines({
    ids: eligibleIds, model: input.model, confirmed, cadenceRuns: scopedRuns,
    exceptions: input.program.instance?.exceptions ?? [], today, eventsBy, params, holderOf, nameOf, scope: "unit",
  });
  const members = [...new Set((input.unitMembers ?? []).filter((m) => subtree.has(m.unitId) && !m.until).map((m) => m.upn))]
    .map((upn) => ({ upn, name: nameOf(upn) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const delivery = (input.unitRoles ?? []).find((r) => r.unitId === unit.id && r.roleId === "delivery-lead");
  const technical = (input.unitRoles ?? []).find((r) => r.unitId === unit.id && r.roleId === "technical-lead");
  const roles = [
    { roleId: "delivery-lead", name: "Delivery lead", gap: !delivery && (unit.kind === "workstream" || unit.kind === "team"), ...(delivery ? { holder: { upn: delivery.upn, name: nameOf(delivery.upn) } } : {}) },
    { roleId: "technical-lead", name: "Technical lead", gap: false, ...(technical ? { holder: { upn: technical.upn, name: nameOf(technical.upn) } } : {}) },
  ];
  const risks: RiskLine[] = [
    ...base.risks.filter((r) => r.kind === "overdue" || r.kind === "asks-unacknowledged"),
    ...cadences.filter((c) => c.status === "missed" && missedIsEscalation(input.model, c.cadenceId))
      .map((c) => ({ id: `cadence:${c.cadenceId}`, kind: "cadence-missed" as const, tone: c.tone, title: `${c.name} did not run`, detail: c.detail })),
  ];
  if (roles[0]!.gap) risks.push({ id: "gap:delivery-lead", kind: "role-unfilled", tone: "bad", title: "Nobody holds delivery lead", detail: "Hard rule 1: one named person per role on this unit" });
  // The roles fraction on a unit is that unit's roles, not the contract's: the page said "4 of 4
  // named" beside a unit whose delivery lead was nobody. The cadences fraction is this unit's lines
  // for the same reason, and the percentage is recomputed over both rather than inherited.
  const namedHere = roles.filter((r) => r.holder).length;
  const conformance: Fraction[] = base.conformance.map((f) => {
    if (f.label === "Roles") return { ...f, n: namedHere, of: roles.length, measured: true, text: `${namedHere} of ${roles.length} named`, tone: roles.some((r) => r.gap) ? "watch" as const : "good" as const };
    if (f.label === "Cadences") return cadenceFraction(cadences);
    return f;
  });
  // The inherited percentage is dropped rather than spread over: it was the contract's, and a unit
  // whose own fractions do not reach two measured dimensions shows none.
  const { conformancePercent: _inherited, ...header } = base.header;
  const percent = conformanceOver(conformance);
  return {
    ...base,
    cadences,
    roles,
    risks,
    contractEvents: [],
    linked: [],
    gaps: [],
    unitCadences: [],
    unitsForWeek: [],
    // The funding split, the contract's own facts and its initiatives are the
    // programme's, not one unit's.
    contractLines: [],
    contract: {},
    initiatives: [],
    conformance,
    header: { ...header, name: unit.name, peopleCount: members.length, ...(percent === undefined ? {} : { conformancePercent: percent }) },
    headline: { label: `This week · ${unit.name} · ${fmtDate(today)}`, sentence: base.headline.sentence },
    unit: { id: unit.id, name: unit.name, kind: unit.kind },
    programName: input.program.name,
    ...(() => {
      const clin = ancestors.map((id) => (input.units ?? []).find((u) => u.id === id)).find((u) => u?.kind === "clin");
      return clin ? { line: { id: clin.id, ...clinLabel(clin) } } : {};
    })(),
    path: ancestorIds(input.units ?? [], unit.id)
      .map((id) => (input.units ?? []).find((u) => u.id === id))
      .filter((u): u is UnitLike => !!u && (u.kind === "workstream" || u.kind === "team"))
      .reverse()
      .map((u) => ({ id: u.id, name: u.name, kind: u.kind })),
    children: (input.units ?? []).filter((u) => u.active && u.parentId === unit.id)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((u) => ({ id: u.id, name: u.name, people: (input.unitMembers ?? []).filter((m) => m.unitId === u.id && !m.until).length })),
    members,
    ruleVersion: STRUCTURE_RULE,
    sources: ["units", "unit_roles", "unit_members", "commitments", "cadence_runs"],
    canSend: false,
  };
}

function draftVisibleOnUnit(d: DraftLike, ctx: {
  viewer: PersonId;
  subtree: Set<string>;
  ancestors: string[];
  programLeads: Set<PersonId>;
  managerOf: Map<PersonId, string | undefined>;
  unitRoles: { unitId: string; upn: PersonId }[];
  artifacts: { id: string; audience?: string | undefined }[];
}): boolean {
  const audience = d.audience ?? ctx.artifacts.find((a) => a.id === d.artifactId)?.audience ?? "team";
  if (audience === "portfolio") return false;
  if (!d.unitId || !ctx.subtree.has(d.unitId)) return false;
  if (audience === "customer") return d.ownerUpn === ctx.viewer || ctx.programLeads.has(ctx.viewer);
  if (audience === "manager") {
    const chain = new Set([...ctx.subtree, ...ctx.ancestors]);
    return d.ownerUpn === ctx.viewer || ctx.unitRoles.some((r) => r.upn === ctx.viewer && chain.has(r.unitId)) || (!!d.ownerUpn && ctx.managerOf.get(d.ownerUpn) === ctx.viewer);
  }
  return true;
}

/**
 * Whether the model says a cadence that did not run is an escalation. Everything else it calls a
 * finding: a standup or a demo that was skipped is worth reporting on the cadence panel, but there
 * is no decision for anybody to take about it, so it stays off the decision list. Read from the
 * model rather than from a list of cadence ids, which would rot the next time the model changes.
 */
function missedIsEscalation(model: ModelLike, cadenceId: string): boolean {
  return model.cadences.find((c) => c.id === cadenceId)?.missedIs === "escalation";
}

/**
 * The Cadences bar, over the lines beside it.
 *
 * Conformance is measured on completed periods only: a schedule is not a result, and the model owner
 * defines the denominator. Stated once because a unit's page and a contract's page both show this
 * bar over their own lines, and the unit's used to inherit the contract's: it measured seventeen
 * program-scoped rules against one unit's runs and read "no period completed yet" directly above a
 * panel reporting six missed standups.
 */
function cadenceFraction(cadences: CadenceLine[]): Fraction {
  const measurable = cadences.filter((c) => c.status !== "excepted" && c.completedCount > 0);
  if (!measurable.length) return { label: "Cadences", measured: false, text: `No period completed yet · ${plural(cadences.length, "cadence")} apply`, tone: "off" };
  const ranOk = measurable.reduce((n, c) => n + c.ranCount, 0);
  const completedPeriods = measurable.reduce((n, c) => n + c.completedCount, 0);
  const unmeasured = cadences.length - measurable.length;
  return {
    label: "Cadences", n: ranOk, of: completedPeriods, measured: true,
    text: `${ranOk} of ${plural(completedPeriods, "completed period")} ran${unmeasured ? ` · ${unmeasured} not yet measured` : ""}`,
    tone: ranOk === completedPeriods ? "good" : "watch",
  };
}

/** A percentage from one dimension is a number without a meaning; two or more measured, or nothing. */
function conformanceOver(fractions: Fraction[]): number | undefined {
  const measured = fractions.filter((f) => f.measured && f.of);
  if (measured.length < 2) return undefined;
  return Math.round((100 * measured.reduce((s, f) => s + f.n!, 0)) / measured.reduce((s, f) => s + f.of!, 0));
}

function cadenceLines(input: {
  ids: string[];
  model: ModelLike;
  confirmed: Commitment[];
  cadenceRuns: CadenceRunLike[];
  exceptions: NonNullable<NonNullable<ProgramLike["instance"]>["exceptions"]>;
  today: string;
  eventsBy: Map<string, Audit[]>;
  params: (id: string) => unknown;
  holderOf: Map<string, PersonId>;
  nameOf: (upn: PersonId | undefined) => string;
  scope: "program" | "unit";
}): CadenceLine[] {
  return input.ids.map((id) => {
    const def = input.model.cadences.find((c) => c.id === id);
    if (!def) return undefined;
    const mine = input.confirmed.filter((c) => c.kind === "cadence" && c.cadenceId === id);
    const runs = input.cadenceRuns.filter((r) => r.cadenceId === id);
    const exception = input.exceptions.find((x) => x.rule === id && (!x.expires || x.expires >= input.today));
    // Closed items give this line a date and a count, never a name. The line used to end "by
    // <owner>", which said a person had run the cadence on the strength of nothing but their
    // holding the role: unordered, possibly from a different run, and simply false once Trellis is
    // closing these on its own evidence (docs/2026-09-26-cadence-evidence-link.md).
    const closedAt = new Map(mine.filter((c) => c.status === "closed").map((c) => [c.id, lastEventAt(input.eventsBy.get(c.id), "closed") ?? c.updatedAt]));
    const ran = [...runs.filter((r) => r.ranAt).map((r) => r.ranAt!), ...closedAt.values()].sort();
    const lastRan = ran.at(-1);
    const openOnes = mine.filter((c) => c.status === "open").sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    const nextDue = openOnes[0]?.dueDate;
    // Periods, not rows. A run marked missed leaves its commitment open and past its date, and a run
    // that ran closes it, so counting runs and items separately reported every period twice: COMET's
    // 105 unit misses read as 210 and its two program misses as four.
    const periods = cadencePeriods({ runs, items: mine, today: input.today, closedAt: (c) => closedAt.get(c.id) });
    const tally = tallyPeriods(periods);
    const excepted = !!exception;
    const missedCount = excepted ? 0 : tally.missed;
    const ranCount = excepted ? 0 : tally.ran;
    const completedCount = excepted ? 0 : tally.completed;
    // Every period the exception covers, a period still running included: it is recorded, and it is
    // not going to be measured either.
    const exceptedCount = excepted ? periods.length : 0;
    const withdrawnCount = excepted ? 0 : tally.withdrawn;
    const withdrawnPart = withdrawnCount ? ` · ${withdrawnCount} withdrawn` : "";
    const holder = input.holderOf.get(def.runsBy);
    const line: CadenceLine = { cadenceId: id, name: def.name, frequency: frequencyLabel(def.frequency, input.params), runsBy: input.model.roles.find((r) => r.id === def.runsBy)?.name ?? def.runsBy, ...(holder ? { holder: input.nameOf(holder) } : {}), status: "not-measured", tone: "off", detail: "", scope: input.scope, missedCount, withdrawnCount, exceptedCount, ranCount, completedCount, ...(lastRan ? { lastRan } : {}), ...(nextDue ? { nextDue } : {}) };
    if (exception) {
      line.status = "excepted";
      // What the exception covers, not only that one is in force. Silently dropping the periods it
      // took out of measurement is how an excepted rule came to read as a clean one on this page
      // while its misses still counted on the portfolio.
      line.detail = [
        `Exception in force${exception.expires ? ` to ${fmtDay(exception.expires)}` : ""}`,
        exception.approvedBy ? `approved by ${input.nameOf(exception.approvedBy)}` : "",
        exceptedCount ? `${plural(exceptedCount, "recorded period")} not measured` : "",
      ].filter(Boolean).join(" · ");
      return line;
    }
    if (missedCount > 0) { line.status = "missed"; line.tone = def.missedIs === "escalation" ? "bad" : "watch"; line.detail = `${missedCount === 1 ? "Not run" : `Missed ${missedCount} times`}${nextDue ? ` · was due ${fmtDateFull(nextDue)}` : ""}${lastRan ? ` · last ran ${fmtDateFull(lastRan)}` : ""}${withdrawnPart} · ${def.missedIs === "escalation" ? "an escalation" : "a finding"} per the model`; return line; }
    if (nextDue) {
      line.status = daysBetween(input.today, nextDue) <= 1 ? "due" : lastRan ? "ran" : "scheduled"; line.tone = line.status === "due" ? "watch" : line.status === "ran" ? "good" : "neutral";
      line.detail = `${lastRan ? `Last ran ${fmtDateFull(lastRan)} · next due` : "Next due"} ${fmtDateFull(nextDue)}${withdrawnPart}`;
      return line;
    }
    if (lastRan) { line.status = "ran"; line.tone = "good"; line.detail = `Last ran ${fmtDateFull(lastRan)}${withdrawnPart}`; return line; }
    // Withdrawn periods did fall due; they are simply no longer Trellis's to judge, and saying none had
    // fallen due would erase the very correction that was made.
    line.detail = withdrawnCount ? `${withdrawnCount === 1 ? "Its one recorded period was" : `All ${withdrawnCount} recorded periods were`} withdrawn` : "No period has fallen due yet";
    return line;
  }).filter((x): x is CadenceLine => !!x);
}

function unitCadenceCounts(input: ProgramViewInput, profile: ModelLike["profiles"][number] | undefined, confirmed: Commitment[], today: string): UnitCadenceCount[] {
  const units = (input.units ?? []).filter((u) => u.active);
  const opt = input.optOuts ?? [];
  const findings = input.unitFindings ?? [];
  const runs = input.cadenceRuns ?? [];
  const exceptions = input.program.instance?.exceptions ?? [];
  return (profile?.cadences ?? []).map((id) => {
    const def = input.model.cadences.find((c) => c.id === id);
    if (!def || def.scope !== "unit") return undefined;
    const kinds = new Set(def.eligibleKinds ?? []);
    const opted = new Set(opt.filter((o) => o.cadenceId === id).map((o) => o.unitId));
    const eligible = units.filter((u) => (u.kind === "workstream" || u.kind === "team") && kinds.has(u.kind) && !opted.has(u.id));
    const eligibleIds = new Set(eligible.map((u) => u.id));
    const latest = new Map<string, CadenceRunLike>();
    for (const r of runs) {
      if (r.cadenceId !== id || !r.unitId || !eligibleIds.has(r.unitId)) continue;
      const prev = latest.get(r.unitId);
      if (!prev || (r.periodStart ?? "") > (prev.periodStart ?? "")) latest.set(r.unitId, r);
    }
    let ran = 0, missed = 0, withdrawn = 0, unfilled = 0;
    for (const u of eligible) {
      const run = latest.get(u.id);
      // Withdrawn is read first and the unit stops there. Falling back to an older success would be a
      // quiet upgrade, and falling through to `unfilled` would blame a role gap for a rule that did
      // not apply (docs/2026-09-26-obligation-states.md).
      if (run?.withdrawnAt) withdrawn += 1;
      else if (run?.ranAt) ran += 1;
      else if (run?.missed) missed += 1;
      else if (findings.some((f) => f.kind === "role-unfilled" && f.cadenceId === id && f.unitId === u.id)) unfilled += 1;
    }
    // The same rule as periods, not as units, so this panel and the portfolio's cadence figure
    // reconcile: "thirty-two of thirty-two units missed" says nothing about how many periods that is,
    // and the portfolio's number is periods. Every row of the rule, not only those on an eligible
    // unit, or the two figures differ by whatever the tree has moved on from.
    const all = cadencePeriods({
      runs: runs.filter((r) => r.cadenceId === id),
      items: confirmed.filter((c) => c.kind === "cadence" && c.cadenceId === id),
      today,
    });
    const periods = tallyPeriods(all);
    // An exception reports no verdict here either. The portfolio leaves an excepted rule out of both
    // its figures and the contract line says "Exception in force", so a tree panel still counting its
    // misses would put the two out of step again, one rule along.
    const excepted = exceptions.some((x) => x.rule === id && (!x.expires || x.expires >= today));
    if (excepted) {
      return {
        cadenceId: id, name: def.name, eligible: eligible.length, ran: 0, missed: 0, withdrawn: 0, unfilled: 0,
        periodsRan: 0, periodsMissed: 0, periodsWithdrawn: 0, periodsOffUnit: 0,
        excepted: true, periodsExcepted: all.length,
      };
    }
    return {
      cadenceId: id, name: def.name, eligible: eligible.length, ran, missed, withdrawn, unfilled,
      periodsRan: periods.ran, periodsMissed: periods.missed, periodsWithdrawn: periods.withdrawn,
      periodsOffUnit: all.filter((p) => !p.unitId || !eligibleIds.has(p.unitId)).length,
      excepted: false, periodsExcepted: 0,
    };
  }).filter((x): x is UnitCadenceCount => !!x);
}

// ------------------------------------------------------------------------------------------------ helpers

/** The week in numbers, no commentary: "12 open commitments, 1 overdue · 4 of 4 roles filled · 12 people". */
function headlineSentence(n: { peopleCount: number; filled: number; rolesTotal: number; open: number; asksWaiting: number; overdue: number; pmMode: boolean }): string {
  const parts = [
    `${n.open} open ${n.open === 1 ? "commitment" : "commitments"}${n.overdue ? `, ${n.overdue} overdue` : ""}`,
    `${n.filled} of ${n.rolesTotal} roles filled`,
    `${n.peopleCount} ${n.peopleCount === 1 ? "person" : "people"}`,
  ];
  if (n.asksWaiting) parts.splice(1, 0, `${n.asksWaiting} customer ${n.asksWaiting === 1 ? "ask" : "asks"} waiting`);
  return parts.join(" · ");
}

function paramReader(model: ModelLike, program: ProgramLike): (id: string) => unknown {
  const instance = program.instance?.parameters ?? {};
  return (id) => instance[id] ?? model.parameters.find((p) => p.id === id)?.default;
}

function frequencyLabel(f: ModelLike["cadences"][number]["frequency"], params: (id: string) => unknown): string {
  switch (f.kind) {
    case "daily": return "Daily";
    case "weekly": { const d = f.weekday ? params(f.weekday) : undefined; return d ? `Weekly, ${cap(String(d))}` : "Weekly"; }
    case "monthly": { const d = f.dayOfMonth ? params(f.dayOfMonth) : undefined; return d ? `Monthly, the ${ordinal(Number(d))}` : "Monthly"; }
    case "per-period-of-performance": return "Per period of performance";
    case "per-contract-event": return `Per contract event: ${(f.event ?? "").replace(/-/g, " ")}`;
    case "per-item-clock": return "Per item clock";
    case "on-trigger": return `On trigger: ${(f.trigger ?? "").replace(/-/g, " ")}`;
    default: return f.kind;
  }
}

function ordinal(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

function groupEvents(events: Audit[]): Map<string, Audit[]> {
  const out = new Map<string, Audit[]>();
  for (const e of [...events].sort((a, b) => a.at.localeCompare(b.at))) out.set(e.commitmentId, [...(out.get(e.commitmentId) ?? []), e]);
  return out;
}

function lastEventAt(evs: Audit[] | undefined, type: Audit["event"]["type"]): Iso | undefined {
  return evs ? [...evs].reverse().find((e) => e.event.type === type)?.at : undefined;
}
